import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import JsonLd from '@/components/feature/JsonLd';
import VideoCard from '@/components/base/VideoCard';
import { getVideoById, getRelatedVideos } from '@/mocks/videos';

function formatNumber(num: number): string {
  if (num >= 10000) {
    return (num / 10000).toFixed(1) + '万';
  }
  return num.toString();
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const video = getVideoById(id || '');
  const relatedVideos = getRelatedVideos(id || '', 4);

  if (!video) {
    return (
      <>
        <Navbar />
        <div className="pt-20 text-center py-20">
          <p className="text-foreground-500">视频不存在</p>
          <Link to="/" className="text-[oklch(var(--primary-500))] text-sm mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: video.categoryName, href: `/category/${video.categorySlug}` },
    { label: video.title },
  ];

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: video.title,
    description: video.description,
    thumbnailUrl: video.thumbnail,
    uploadDate: video.publishDate,
    author: {
      '@type': 'Person',
      name: video.authorName,
    },
    interactionStatistic: [
      {
        '@type': 'InteractionCounter',
        interactionType: 'https://schema.org/WatchAction',
        userInteractionCount: video.views,
      },
      {
        '@type': 'InteractionCounter',
        interactionType: 'https://schema.org/LikeAction',
        userInteractionCount: video.likes,
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <JsonLd data={articleJsonLd} />

      <header>
        <Navbar />
      </header>

      <main className="pt-20">
        <Breadcrumb items={breadcrumbItems} />

        <article className="w-full px-4 md:px-6 py-6">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <div className="flex-1 min-w-0">
              {/* Video Player Area */}
              <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-foreground-950 mb-6">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover"
                  title={video.title}
                />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-[oklch(var(--primary-500))]/90 flex items-center justify-center cursor-pointer hover:bg-[oklch(var(--primary-500))] transition-colors">
                    <i className="ri-play-fill text-background-50 text-4xl ml-1.5"></i>
                  </div>
                </div>
                <span className="absolute bottom-4 right-4 px-3 py-1 bg-black/70 text-background-50 text-sm rounded whitespace-nowrap">
                  {video.duration}
                </span>
              </div>

              {/* Title */}
              <h1 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                {video.title}
              </h1>

              {/* Stats Bar */}
              <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
                <div className="flex items-center gap-4">
                  <img
                    src={video.authorAvatar}
                    alt={video.authorName}
                    className="w-10 h-10 rounded-full object-cover"
                    loading="lazy"
                  />
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{video.authorName}</p>
                    <p className="text-xs text-foreground-500">{video.publishDate} 发布</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1.5 text-foreground-600">
                    <button className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center cursor-pointer hover:bg-[oklch(var(--primary-500)/0.1)] transition-colors">
                      <i className="ri-thumb-up-line text-lg"></i>
                    </button>
                    <span className="text-sm font-medium">{formatNumber(video.likes)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-foreground-600">
                    <button className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center cursor-pointer hover:bg-[oklch(var(--accent-500)/0.1)] transition-colors">
                      <i className="ri-share-forward-line text-lg"></i>
                    </button>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="p-5 rounded-xl bg-background-100 border border-background-200/70 mb-8">
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">视频简介</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{video.description}</p>
                <div className="flex items-center gap-2 mt-4 flex-wrap">
                  {video.tags.map((tag) => (
                    <span key={tag} className="text-xs px-3 py-1 rounded-full bg-background-200/70 text-foreground-600 whitespace-nowrap">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar - Related Videos */}
            <aside className="lg:w-[380px] flex-shrink-0">
              <h3 className="text-lg font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                相关推荐
              </h3>
              <div className="flex flex-col gap-4">
                {relatedVideos.map((rv) => (
                  <VideoCard
                    key={rv.id}
                    id={rv.id}
                    title={rv.title}
                    thumbnail={rv.thumbnail}
                    authorName={rv.authorName}
                    authorAvatar={rv.authorAvatar}
                    views={rv.views}
                    likes={rv.likes}
                    duration={rv.duration}
                    categoryName={rv.categoryName}
                    categorySlug={rv.categorySlug}
                  />
                ))}
              </div>
            </aside>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}